import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { RBACProvider } from './contexts/RBACContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { POITableProvider } from './contexts/POITableContext';
import { isEmbedPath, parseEmbedParams } from './embed/embedParams';
import './styles/global.css';
// theme.css (the design-system classes + legacy tokens) must load at the app entry —
// it previously entered the bundle only as a side effect of SitesPage importing it,
// so removing/lazy-loading SitesPage would have silently dropped the entire theme.
// Order matters: theme.css after global.css preserves the historical cascade.
import './styles/theme.css';

// Basic CSS reset
const style = document.createElement('style');
style.innerHTML = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    height: 100vh;
    overflow: hidden;
  }

  #root {
    height: 100vh;
    width: 100vw;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

// Create React root and render app
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// External embed surface (/embed#token=...) — a separate, chrome-less React
// tree for partner iframes (see docs/SPECIFICATIONS.md "External Embed
// Integration"). Detected before the normal app renders; the embed bundle is
// code-split so the main app pays nothing for it.
const embedParamsForBoot = isEmbedPath(window.location.pathname)
  ? parseEmbedParams(window.location.hash)
  : null;

if (isEmbedPath(window.location.pathname)) {
  // locale/theme deep-link params must be seeded BEFORE the providers read
  // localStorage in their useState initializers.
  if (embedParamsForBoot?.locale) {
    try { localStorage.setItem('mabat3d-language', embedParamsForBoot.locale); } catch {}
  }
  if (embedParamsForBoot?.theme) {
    try { localStorage.setItem('mabat3d-theme', embedParamsForBoot.theme); } catch {}
  }

  if (!embedParamsForBoot) {
    root.render(
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: '#888' }}>
        Missing embed token.
      </div>
    );
  } else {
    import(/* webpackChunkName: "embed-app" */ './embed/EmbedApp').then(({ default: EmbedApp }) => {
      root.render(
        <React.StrictMode>
          <EmbedApp params={embedParamsForBoot} />
        </React.StrictMode>
      );
    });
  }
} else {
  // Datadog RUM — main app only, as early as possible (session/timing capture).
  import('./services/datadogInit').then(({ initDatadogRum }) => initDatadogRum()).catch(() => {});
  // P3 observability: Core Web Vitals → usage-events pipeline. Main app only —
  // embed sessions are untracked by design (P2 decision).
  import('./services/webVitals').then(({ initWebVitals }) => initWebVitals()).catch(() => {});

  root.render(
    <React.StrictMode>
      <TranslationProvider>
        <ThemeProvider>
          <AuthProvider>
            <RBACProvider>
              <POITableProvider>
                <App />
              </POITableProvider>
            </RBACProvider>
          </AuthProvider>
        </ThemeProvider>
      </TranslationProvider>
    </React.StrictMode>
  );
}